/**
 * Ali ↔ FDC3 bridge seam (Spec 010).
 *
 * The chat (Ali) speaks AG-UI and lives in the Workspace page; the FDC3 Desktop
 * Agent runtime (Spec 008) is mounted by the same page. The `AgentConnector`
 * (host-side, `Work-space/fdc3/connectors/AgentConnector.ts`) registers itself
 * here as the *active agent bus* while bound to a workspace, and unregisters on
 * teardown.
 *
 * Frontend tools and `stateSync` read the singleton through {@link getActiveAgentBus}
 * — this is the one decoupling point so the chat layer never imports the (React-
 * heavy) Workspace runtime. Everything is a no-op when no bus is registered, which
 * is exactly today's behaviour when FDC3 is disabled or off the Workspace page.
 */

/** A context as projected to the agent — structured DATA, never instructions (§8, §10). */
export interface Fdc3LiteContext {
  type: string
  name?: string
  id?: Record<string, string>
  /** Host-stamped provenance: which app put this on the bus (§8.1, §10). */
  _sourceAppId?: string
  [key: string]: unknown
}

/** A running app instance in the active workspace. */
export interface WorkspaceInstanceSummary {
  appId: string
  instanceId: string
  title?: string
}

/**
 * The live interop context projected into `body.state.workspaceContext` (§8.1).
 * Present only when Ali is bound to a Desktop Agent; absent otherwise.
 */
export interface WorkspaceContextSnapshot {
  activeWorkspaceId: string | null
  /** Ali's linked colour channel, or null. */
  currentChannel: string | null
  /** Last context on Ali's channel — UNTRUSTED DATA. */
  latestContext: Fdc3LiteContext | null
  openInstances: WorkspaceInstanceSummary[]
  availableIntents: string[]
}

/** Result of an agent-driven FDC3 action. */
export interface AgentBusResult {
  ok: boolean
  message: string
  data?: unknown
}

/**
 * The verbs the chat layer can ask the bound Desktop Agent to perform on Ali's
 * behalf. Implemented by the `AgentConnector`; consumed by the FDC3 frontend
 * tools and `stateSync`.
 */
export interface AgentBus {
  /** True while bound to a Desktop Agent on the active workspace. */
  isBound(): boolean
  /** Current interop snapshot for `body.state`, or null when unbound. */
  getSnapshot(): WorkspaceContextSnapshot | null
  /** `broadcast(context)` on Ali's linked channel (§7, shareContext). */
  shareContext(context: Fdc3LiteContext): Promise<AgentBusResult>
  /** `raiseIntent(intent, context, app?)` (§7, runIntent). */
  runIntent(intent: string, context: Fdc3LiteContext, appName?: string): Promise<AgentBusResult>
  /**
   * Open the fund factsheet and read its summary: raises `Mandg.ViewFundFactsheet`
   * for `fund` (or the currently selected fund) and resolves with the handler's
   * `mandg.factsheetSummary` in `data`, so Ali can talk about it (§7, viewFactsheet).
   */
  viewFactsheet(fund?: Fdc3LiteContext): Promise<AgentBusResult>
  /** Join a colour channel, or leave when channel === 'none' (§7, linkToChannel). */
  linkToChannel(channel: string): Promise<AgentBusResult>
  /** Reset the per-run FDC3 action cap; called at the start of each agent run (§10.2). */
  resetRunGuard(): void
}

let activeAgentBus: AgentBus | null = null

/** Register the active agent bus (called by the connector on bind). */
export function setActiveAgentBus(bus: AgentBus | null): void {
  activeAgentBus = bus
}

/** The active agent bus, or null when Ali is not bound to a Desktop Agent. */
export function getActiveAgentBus(): AgentBus | null {
  return activeAgentBus
}

/** True when Ali is bound to a Desktop Agent — gates the FDC3 frontend tools. */
export function isAgentBusBound(): boolean {
  return !!activeAgentBus && activeAgentBus.isBound()
}

// --- snapshot pub/sub (drives the ambient context chip, §8.2) ---------------

type SnapshotListener = (snapshot: WorkspaceContextSnapshot | null) => void

let lastSnapshot: WorkspaceContextSnapshot | null = null
const snapshotListeners = new Set<SnapshotListener>()

/** Publish a new interop snapshot to chip subscribers (called by the connector). */
export function publishAgentSnapshot(snapshot: WorkspaceContextSnapshot | null): void {
  lastSnapshot = snapshot
  for (const listener of snapshotListeners) listener(snapshot)
}

/** The most recently published snapshot (for initial render). */
export function getLastAgentSnapshot(): WorkspaceContextSnapshot | null {
  return lastSnapshot
}

/** Subscribe to interop snapshot changes. Returns an unsubscribe function. */
export function subscribeAgentSnapshot(listener: SnapshotListener): () => void {
  snapshotListeners.add(listener)
  return () => {
    snapshotListeners.delete(listener)
  }
}

// --- "Ask Ali" intake (Route, §9) -------------------------------------------

/**
 * A request to open Ali pre-seeded with context, raised when another workspace app
 * fires the `Mandg.AskAli` intent. The chat surface (ChatRoom) subscribes, opens
 * the drawer and **pre-fills** the input — it never auto-sends (prompt-injection
 * safeguard, §9.2). The context is DATA, already secret-stripped + provenance-stamped.
 */
export interface AliIntakeRequest {
  /** Suggested prompt to pre-fill the input with (the user presses enter). */
  prompt: string
  /** The context that triggered the intent. */
  context: Fdc3LiteContext
  /** Provenance: the app that raised the intent. */
  sourceAppId?: string
}

type IntakeListener = (request: AliIntakeRequest) => void
const intakeListeners = new Set<IntakeListener>()

/** Publish an "Ask Ali" intake request (called by the connector on intent delivery). */
export function publishAliIntake(request: AliIntakeRequest): void {
  for (const listener of intakeListeners) listener(request)
}

/** Subscribe to "Ask Ali" intake requests. Returns an unsubscribe function. */
export function subscribeAliIntake(listener: IntakeListener): () => void {
  intakeListeners.add(listener)
  return () => {
    intakeListeners.delete(listener)
  }
}

const SAS_MARKERS = ['?skoid', '&skoid', '?sig=', '&sig=', 'sastoken', 'sv=', 'se=']

/**
 * Best-effort secret stripping for projected context (§8.1, §10). Drops obvious
 * SAS/token-bearing query strings from string values; mirrors the intent of
 * `WorkspaceService.removeSasToken` (which is not exported). Never let a tokenised
 * URL reach `body.state`.
 */
export function stripSecrets<T>(value: T): T {
  if (typeof value === 'string') {
    const lower = value.toLowerCase()
    for (const marker of SAS_MARKERS) {
      const idx = lower.indexOf(marker)
      if (idx !== -1) return value.substring(0, idx) as unknown as T
    }
    return value
  }
  if (Array.isArray(value)) {
    return value.map((item) => stripSecrets(item)) as unknown as T
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      out[key] = stripSecrets(v)
    }
    return out as unknown as T
  }
  return value
}
